import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from "@angular/router";
import { Observable } from "rxjs";

import { ProfileService } from "./profile.service";
import { AuthService } from "./auth.service";

const ADMIN_ONLY_PATHS = [
  "useredit",
  "userdetail",
  "showjob",
  "addjob",
  "jobsoverview",
  "phoneappointment",
  "contractoverview",
  "payrolls",
];

const BEGINNER_OR_ADMIN_PATHS = [
  "marketoverview",
  "jobdetail",
  "positiondetail",
  "chronicarichive",
  "chronicnow",
  "chronicfuture",
];

@Injectable({
  providedIn: "root",
})
export class AuthGuardService {
  constructor(
    private router: Router,
    public profileService: ProfileService,
    private authService: AuthService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): boolean | Observable<boolean> | Promise<boolean> {
    return this.checkAccess(next);
  }

  private async checkAccess(next: ActivatedRouteSnapshot): Promise<boolean> {
    const path = next.routeConfig?.path ?? "(no-routeConfig-path)";
    const guardLabel = `[PERF][AuthGuard][${path}]`;
    const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();

    console.log(`${guardLabel} start`);

    const tAuth0 = typeof performance !== "undefined" ? performance.now() : Date.now();
    const user = await this.authService.waitForAuth();
    const tAuth1 = typeof performance !== "undefined" ? performance.now() : Date.now();
    console.log(`${guardLabel} waitForAuth ${(tAuth1 - tAuth0).toFixed(1)}ms`);

    if (!user) {
      const t1 = typeof performance !== "undefined" ? performance.now() : Date.now();
      console.log(`${guardLabel} blocked (no user) total ${(t1 - t0).toFixed(1)}ms`);
      this.router.navigate(["/login"]);
      return false;
    }

    if (!this.profileService.isProfileReady()) {
      const tProf0 = typeof performance !== "undefined" ? performance.now() : Date.now();
      await this.profileService.ensureProfileLoaded();
      const tProf1 = typeof performance !== "undefined" ? performance.now() : Date.now();
      console.log(`${guardLabel} ensureProfileLoaded ${(tProf1 - tProf0).toFixed(1)}ms`);
    }

    if (path === "(no-routeConfig-path)") {
      const t1 = typeof performance !== "undefined" ? performance.now() : Date.now();
      console.log(`${guardLabel} allowed total ${(t1 - t0).toFixed(1)}ms`);
      return true;
    }

    if (ADMIN_ONLY_PATHS.includes(path) && !this.profileService.isAdminSync()) {
      const t1 = typeof performance !== "undefined" ? performance.now() : Date.now();
      console.log(`${guardLabel} blocked (admin-only) total ${(t1 - t0).toFixed(1)}ms`);
      this.router.navigate(["/profiletabs"]);
      return false;
    }

    if (
      path === "scheduleappointment" &&
      this.profileService.userProfile.phoneAppointment !== "" &&
      !this.profileService.isAdminSync()
    ) {
      const t1 = typeof performance !== "undefined" ? performance.now() : Date.now();
      console.log(`${guardLabel} blocked (scheduleappointment) total ${(t1 - t0).toFixed(1)}ms`);
      this.router.navigate(["/profiletabs"]);
      return false;
    }

    if (
      BEGINNER_OR_ADMIN_PATHS.includes(path) &&
      !(this.profileService.isAdminSync() || this.profileService.isBeginnerSync())
    ) {
      const t1 = typeof performance !== "undefined" ? performance.now() : Date.now();
      console.log(`${guardLabel} blocked (beginner/admin-only) total ${(t1 - t0).toFixed(1)}ms`);
      this.router.navigate(["/profiletabs"]);
      return false;
    }

    const t1 = typeof performance !== "undefined" ? performance.now() : Date.now();
    console.log(`${guardLabel} allowed total ${(t1 - t0).toFixed(1)}ms`);
    return true;
  }
}
